import * as React from 'react';
import { compose, setDisplayName, withHandlers, withState, lifecycle, withProps } from 'recompose';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { deepEqual } from 'util/deepEqual';
import { State } from 'reducers';
import { DeviceIndex, DeviceIdMap, DeviceMap, Camera } from 'types';
import { ApiError } from 'api/fetch';
import { postSeekRequest, getCameraAddOnConfig, putCameraAddOnConfig, XmlConfigMap } from 'api/camera';
import { getDeviceIdByDeviceIndex, getDevices, isCamera } from 'selectors/deviceSelectors';
import { Point, ReportSettings, ReportSettingsChangeHandler, fencesToPoints, pointsToFences } from 'logic/reports';
import { extractFences, overwriteFences } from 'logic/addOnConfig';
import { Button, ButtonWithSpinner, Loading, WarningMessage } from 'ui/shared';
import { Fade, FadeCollapse } from 'ui/transitions';
import { PtzLocationPicker } from './PtzLocationPicker';
import { LineEditor } from './LineEditor';
import { CameraPicker } from './CameraPicker';

export type OwnProps = {
    reportSettings: Readonly<ReportSettings>;
    onChange: ReportSettingsChangeHandler;
};

const RawLineSection = ({
    points, canDeletePoint, base64image, isLoadingImage, cameras, locations,
    showPtzLocations, selectedIndex, selectedCamera, selectedLocation,
    linesMismatchWarningVisible, loadingLines, savingLines, handleClickRefreshImage,
    handleChangeSelectedCamera, handleChangeSelectedLocation,
    handleSelectPoint, handleChangePoint, handleAddPoint, handleClickDeletePoint,
    handleClickReverseDirection, handleClickLoadLines, handleClickSaveLines,
    t,
}: OwnProps & RecomposeProps & TranslateProps) =>
    <div id="line-section">
        <div>
            <label className="camera-label">
                { t('WC_CAMERA_PROMPT') }
                { cameras.length === 0
                    ? <span className="no-cameras">
                        { t('1677') }
                    </span>
                    : <CameraPicker
                        id="edit-report-line-camera"
                        cameras={ cameras }
                        value={ selectedCamera }
                        onChange={ handleChangeSelectedCamera }
                        disabled={ loadingLines || savingLines }
                    />
                }
            </label>
            <Fade visible={ showPtzLocations } duration={ 200 } element="span">
                <label className="camera-label">
                    <span>
                        { t('496') }
                    </span>
                    <PtzLocationPicker
                        id="edit-report-line-camera-location"
                        locations={ locations }
                        value={ selectedLocation }
                        onChange={ handleChangeSelectedLocation }
                        disabled={ loadingLines || savingLines || !showPtzLocations }
                    />
                </label>
            </Fade>
        </div>

        <FadeCollapse visible={ cameras.length > 0 && linesMismatchWarningVisible } duration={ 400 } height="2em">
            <WarningMessage
                textKey="WC_E_REPORT_LINES_MISMATCH"
            />
        </FadeCollapse>

        <FadeCollapse visible={ cameras.length > 0 } duration={ 400 } height="2.25em">
            <div className="button-bar">
                <Button
                    id="line-delete-point"
                    textKey="WC_REPORT_LINE_DELETE_POINT"
                    onClick={ handleClickDeletePoint }
                    disabled={ !canDeletePoint || loadingLines || savingLines }
                />
                <Button
                    id="line-reverse-direction"
                    textKey="6.1237.3428"
                    onClick={ handleClickReverseDirection }
                    disabled={ loadingLines || savingLines }
                />
                <Fade visible={ linesMismatchWarningVisible } duration={ 400 } element="span">
                    <ButtonWithSpinner
                        id="load-lines-from-config"
                        textKey="WC_REPORT_LINES_LOAD"
                        titleKey="WC_REPORT_LINES_LOAD_TOOLTIP"
                        onClick={ handleClickLoadLines }
                        spinning={ loadingLines }
                        disabled={ savingLines }
                    />
                    <ButtonWithSpinner
                        id="save-lines-to-config"
                        textKey="WC_REPORT_LINES_SAVE"
                        titleKey="WC_REPORT_LINES_SAVE_TOOLTIP"
                        onClick={ handleClickSaveLines }
                        spinning={ savingLines }
                        disabled={ loadingLines }
                    />
                </Fade>
                <span className="change-image">
                    <ButtonWithSpinner
                        id="line-refresh-image"
                        textKey="1108"
                        onClick={ handleClickRefreshImage }
                        spinning={ isLoadingImage }
                    />
                </span>
            </div>
        </FadeCollapse>
        
        <FadeCollapse visible={ cameras.length > 0 } duration={ 400 } height="40em" className="editor-host">
            <LineEditor
                points={ points }
                selectedIndex={ selectedIndex }
                image={ base64image }
                onChange={ handleChangePoint }
                onSelect={ handleSelectPoint }
                onAddPoint={ handleAddPoint }
                disabled={ isLoadingImage || loadingLines || savingLines }
                showLines={ !!base64image }
            />
            <Fade visible={ isLoadingImage || loadingLines } duration={ 400 } unmountOnExit={ true }>
                <Loading textKey="6.24407.4553" size="small"/>
            </Fade>
        </FadeCollapse>
    </div>
;

type StateProps = {
    allDevices: DeviceMap;
    deviceIdByDeviceIndex: DeviceIdMap;
};
type RecomposeProps = {
    points: ReadonlyArray<Point>;
    canDeletePoint: boolean;
    base64image: string | null;
    isLoadingImage: boolean;
    cameras: ReadonlyArray<DeviceIndex>;
    locations: ReadonlyArray<string>;
    selectedIndex: number | null;
    selectedCamera: DeviceIndex | null;
    selectedLocation: string;
    linesMismatchWarningVisible: boolean;
    loadingLines: boolean;
    savingLines: boolean;
    showPtzLocations: boolean;
    handleChangeSelectedCamera: (i: DeviceIndex) => void;
    handleChangeSelectedLocation: (l: string) => void;
    handleClickRefreshImage: () => void;
    handleChangePoint: (index: number, pt: Point) => void;
    handleSelectPoint: (index: number | null) => void;
    handleAddPoint: (pt: Point) => void;
    handleClickDeletePoint: () => void;
    handleClickReverseDirection: () => void;
    handleClickLoadLines: () => void;
    handleClickSaveLines: () => void;
    loadLines: (currentPoints: ReadonlyArray<Point>) => Promise<void>;
};
type _RecomposeProps = RecomposeProps & {
    _analyticsPoints: ReadonlyArray<Point> | null;
    _analyticsXmlMap: XmlConfigMap | null;
    _setPoints: (z: ReadonlyArray<Point>) => void;
    _setAnalyticsPoints: (z: ReadonlyArray<Point> | null) => void;
    _setAnalyticsXmlMap: (x: XmlConfigMap | null) => void;
    _setSelectedIndex: (n: number | null) => void;
    _setSelectedCamera: (id: DeviceIndex | null) => void;
    _setSelectedLocation: (l: string) => void;
    _setIsLoadingImage: (b: boolean) => void;
    _setImage: (i: string | null) => void;
    _setLinesMismatchWarningVisible: (b: boolean) => void;
    _setLoadingLines: (b: boolean) => void;
    _setSavingLines: (b: boolean) => void;
};


const getCameraFromProps = (props: StateProps & RecomposeProps): Camera | null => {
    const { allDevices, deviceIdByDeviceIndex, selectedCamera } = props;
    const deviceId = deviceIdByDeviceIndex[`${selectedCamera}`];
    if (!deviceId) {
        return null;
    }
    const device = allDevices[deviceId];
    if (!isCamera(device)) {
        return null;
    }
    return device as Camera;
};

const defaultLocation = '';
const defaultLocations: string[] = [ defaultLocation ]; // Contains the default, empty location

const getLocationsFromProps = (props: StateProps & _RecomposeProps): ReadonlyArray<string> => {
    // TODO
    //const camera = getCameraFromProps(props);
    //return camera && camera.ptzLocations || defaultLocations;
    return defaultLocations; // KILL: in AIR-35959.
};

const getShowPtzLocationsFromProps = (props: StateProps & _RecomposeProps): boolean => {
    // TODO
    //const camera = getCameraFromProps(props);
    //return !!camera && camera.deviceType === 'PTZ';
    return false; // KILL: in AIR-35959
};


const mapStateToProps = (state: State) => ({
    allDevices: getDevices(state) || {},
    deviceIdByDeviceIndex: getDeviceIdByDeviceIndex(state),
});

export const LineSection = compose<OwnProps & RecomposeProps & TranslateProps, OwnProps>(
    setDisplayName('LineSection'),
    translate(),
    connect<StateProps, {}, OwnProps>(mapStateToProps),
    withProps(({ reportSettings }: OwnProps) => ({
        cameras: reportSettings.devices.map(d => d.deviceIndex),
    })),
    withState('selectedCamera', '_setSelectedCamera', ({ reportSettings }: OwnProps) => {
        const [ firstDevice ] = reportSettings.devices;
        if (typeof firstDevice === 'undefined') {
            return null;
        }
        return firstDevice.deviceIndex;
    }),
    withState('selectedLocation', '_setSelectedLocation', (props: OwnProps & StateProps & RecomposeProps) => {
        // TODO
        //const camera = getCameraFromProps(props);
        //return camera && camera.ptzLocations && camera.ptzLocations[0] || defaultLocation;
        return defaultLocation; // KILL: in AIR-35959
    }),
    withState('points', '_setPoints', (props: OwnProps & StateProps & RecomposeProps) => {
        const camera = getCameraFromProps(props);
        if (camera) {
            const fences = props.reportSettings.deviceFences(camera.deviceIndex, camera.streams[0]);
            return fencesToPoints(fences, camera.streams[0]);
        }
        return [];
    }),
    withState('_analyticsXmlMap', '_setAnalyticsXmlMap', null),
    withState('_analyticsPoints', '_setAnalyticsPoints', null),
    withState('isLoadingImage', '_setIsLoadingImage', false),
    withState('base64image', '_setImage', null),
    withState('selectedIndex', '_setSelectedIndex', null),
    withState('loadingLines', '_setLoadingLines', false),
    withState('savingLines', '_setSavingLines', false),
    withState('linesMismatchWarningVisible', '_setLinesMismatchWarningVisible', false),
    withProps(({ points }) => ({
        canDeletePoint: points && points.length > 2,
    })),
    withProps<Partial<RecomposeProps>, StateProps & _RecomposeProps>(props => ({
        locations: getLocationsFromProps(props),
        showPtzLocations: getShowPtzLocationsFromProps(props),
    })),
    withProps<Partial<RecomposeProps>, StateProps & _RecomposeProps>(props => ({
        async loadLines(currentPoints) {
            const {
                selectedCamera, _setLinesMismatchWarningVisible,
                _setLoadingLines, _setAnalyticsXmlMap, _setAnalyticsPoints,
                locations, _setSelectedLocation,
            } = props;

            if (!selectedCamera) {
                return;
            }
            _setLoadingLines(true);
            _setAnalyticsPoints(null);
            _setAnalyticsXmlMap(null);
            _setLinesMismatchWarningVisible(false);
            try {
                const camera = getCameraFromProps(props);
                if (!camera) {
                    throw new ApiError('6.1237.3262'); // Camera not found
                }
                const xmlMap = await getCameraAddOnConfig(camera.deviceId, 'Intel_VE180');
                _setAnalyticsXmlMap(xmlMap);

                const location =
                    locations
                        ? locations[0]
                        : '';
                _setSelectedLocation(location);
                const xml = xmlMap[location];

                if (xml) {
                    const fences = extractFences(xml, camera.streams[0]);
                    const analyticsPoints = fencesToPoints(fences, camera.streams[0]);
                    _setAnalyticsPoints(analyticsPoints);

                    const mismatch = !deepEqual(currentPoints, analyticsPoints);
                    _setLinesMismatchWarningVisible(mismatch);
                }
            }
            catch (err) {
                console.error(err);
            }
            finally {
                _setLoadingLines(false);
            }
        },
    })),
    withHandlers<OwnProps & StateProps & _RecomposeProps, Partial<RecomposeProps>>({
        handleChangeSelectedCamera: ({ _setSelectedCamera }) => _setSelectedCamera,
        handleChangeSelectedLocation: props => (l: string) => {
            const { _setSelectedLocation, _analyticsXmlMap, _setAnalyticsPoints } = props;

            _setSelectedLocation(l);
            if (_analyticsXmlMap) {
                const camera = getCameraFromProps(props);
                const xml = _analyticsXmlMap[l] || null;
                if (xml && camera) {
                    const fences = extractFences(xml, camera.streams[0]);
                    const points = fencesToPoints(fences, camera.streams[0]);
                    _setAnalyticsPoints(points);
                }
                else {
                    _setAnalyticsPoints(null);
                }
            }
        },
        handleClickRefreshImage: ({ selectedCamera, deviceIdByDeviceIndex, _setIsLoadingImage, _setImage }) => async () => {
            const deviceId = deviceIdByDeviceIndex[`${selectedCamera}`];
            if (!deviceId) {
                // No device, so wipe out the image
                _setImage(null);
                console.error('Skipping refresh because no device selected');
                return;
            }

            _setIsLoadingImage(true);
            try {
                const { frame } = await postSeekRequest(deviceId, 0, 'to', 0, false);
                _setImage(frame);
            }
            catch (err) {
                _setImage(null);
                console.error(err);
            }
            finally {
                _setIsLoadingImage(false);
            }
        },
        handleClickLoadLines: props => () => {
            const { onChange, _analyticsPoints, _setLinesMismatchWarningVisible, _setPoints } = props;
            const camera = getCameraFromProps(props);
            if (_analyticsPoints && camera) {
                _setPoints(_analyticsPoints);
                _setLinesMismatchWarningVisible(false);
                const fences = pointsToFences(_analyticsPoints);
                onChange(reportSettings => reportSettings.setDeviceFences(camera.deviceIndex, camera.streams[0], fences));
            }
        },
        handleClickSaveLines: props => async () => {
            const {
                points, _analyticsXmlMap, _setAnalyticsXmlMap,
                _setAnalyticsPoints, _setLinesMismatchWarningVisible, _setSavingLines, selectedLocation,
            } = props;

            const camera = getCameraFromProps(props);
            if (!camera || !_analyticsXmlMap) {
                return;
            }

            _setSavingLines(true);
            try {
                const xml =
                    typeof _analyticsXmlMap[selectedLocation] === 'undefined'
                        ? _analyticsXmlMap[Object.keys(_analyticsXmlMap)[0] || defaultLocation] // KILL: in AIR-35959. Just a hack to support PTZ for now (use the first location)
                        : _analyticsXmlMap[selectedLocation];
                const fences = pointsToFences(points);
                const newXmlMap = {
                    ..._analyticsXmlMap,
                    [selectedLocation]: overwriteFences(xml, camera.streams[0], fences),
                };

                await putCameraAddOnConfig(camera.deviceId, 'Intel_VE180', newXmlMap);

                _setAnalyticsPoints(points);
                _setAnalyticsXmlMap(newXmlMap);
                _setLinesMismatchWarningVisible(false);
            }
            catch (err) {
                console.error(err);
            }
            finally {
                _setSavingLines(false);
            }
        },
        handleChangePoint: props => (index: number, pt: Point) => {
            const camera = getCameraFromProps(props);
            if (!camera) {
                return;
            }
            const { onChange, points, _setPoints } = props;
            const newPoints = points.map((p, i) => i === index ? pt : p);
            _setPoints(newPoints);

            const fences = pointsToFences(newPoints);
            onChange(reportSettings => reportSettings.setDeviceFences(camera.deviceIndex, camera.streams[0], fences));
        },
        handleAddPoint: props => (pt: Point) => {
            const camera = getCameraFromProps(props);
            if (!camera) {
                return;
            }
            const { onChange, points, _setPoints, _setSelectedIndex } = props;
            const newPoints = [ ...points, pt ];
            _setPoints(newPoints);
            _setSelectedIndex(points.length);

            const fences = pointsToFences(newPoints);
            onChange(reportSettings => reportSettings.setDeviceFences(camera.deviceIndex, camera.streams[0], fences));
        },
        handleClickDeletePoint: props => () => {
            const camera = getCameraFromProps(props);
            if (!camera) {
                return;
            }
            const { onChange, points, _setPoints, selectedIndex, _setSelectedIndex } = props;
            if (selectedIndex === null) {
                return;
            }
            const newPoints = [
                ...points.slice(0, selectedIndex),
                ...points.slice(selectedIndex + 1),
            ];
            _setPoints(newPoints);
            _setSelectedIndex(Math.min(selectedIndex, newPoints.length - 1));

            const fences = pointsToFences(newPoints);
            onChange(reportSettings => reportSettings.setDeviceFences(camera.deviceIndex, camera.streams[0], fences));
        },
        handleClickReverseDirection: props => () => {
            const camera = getCameraFromProps(props);
            if (!camera) {
                return;
            }
            const { onChange, points, _setPoints } = props;
            const newPoints = [ ...points ].reverse();
            _setPoints(newPoints);

            const fences = pointsToFences(newPoints);
            onChange(reportSettings => reportSettings.setDeviceFences(camera.deviceIndex, camera.streams[0], fences));
        },
        handleSelectPoint: ({ _setSelectedIndex }) => _setSelectedIndex,
    }),
    lifecycle<OwnProps & StateProps & _RecomposeProps, {}>({
        componentDidMount() {
            const { selectedCamera, loadLines, handleClickRefreshImage } = this.props;

            // Kick off an image request if the camera is already set
            if (selectedCamera) {
                loadLines(this.props.points);
                handleClickRefreshImage();
            }
        },
        componentDidUpdate(oldProps) {
            const {
                reportSettings, selectedCamera, _setSelectedCamera, points, _setPoints, _setImage,
                _analyticsPoints, loadLines, handleClickRefreshImage, _setLinesMismatchWarningVisible,
            } = this.props;

            // Re-initialize our state if the incoming report settings have different
            // devices (i.e. the user added or removed a camera).
            const { devices } = reportSettings;
            if (JSON.stringify(devices) !== JSON.stringify(oldProps.reportSettings.devices)) {
                // Select the first camera if we don't have a selected camera yet
                if (typeof selectedCamera !== 'number' && devices.length) {
                    _setSelectedCamera(devices[0].deviceIndex);
                }
                // Remove the selection if the selected camera does not exist in the devices collection
                else if (typeof selectedCamera === 'number' && devices.filter(({ deviceIndex }) => deviceIndex === selectedCamera).length === 0) {
                    _setSelectedCamera(devices.length ? devices[0].deviceIndex : null);
                }
            }

            // Set the new points if the selected camera changed
            const camera = getCameraFromProps(this.props);
            if (camera && selectedCamera !== oldProps.selectedCamera) {
                const fences = reportSettings.deviceFences(camera.deviceIndex, camera.streams[0]);
                const newPoints = fencesToPoints(fences, camera.streams[0]);
                _setPoints(newPoints);
                loadLines(newPoints);
            }

            // Kick off an image request and lines request if the selected camera has changed
            if (selectedCamera && selectedCamera !== oldProps.selectedCamera) {
                _setImage(null);
                handleClickRefreshImage();
            }

            if (_analyticsPoints !== null &&
                (
                    _analyticsPoints !== oldProps._analyticsPoints ||
                    points !== oldProps.points
                )
            ) {
                const mismatch = !deepEqual(points, _analyticsPoints);
                _setLinesMismatchWarningVisible(mismatch);
            }
        },
    }),
)(RawLineSection);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/line/LineSection.tsx